/**
 * Melian Dialogue — Web Push client utilities.
 *
 * Handles VAPID key retrieval, browser permission dance, PushManager
 * subscribe/unsubscribe, and posting the subscription to our backend.
 */
import { api } from "@/lib/api";

export const isPushSupported = () =>
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;

export const notificationPermission = () =>
    (typeof Notification === "undefined" ? "default" : Notification.permission);

function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const rawData = window.atob(base64);
    const output = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; i++) output[i] = rawData.charCodeAt(i);
    return output;
}

/**
 * Full subscribe flow:
 *   1) request permission
 *   2) get VAPID public key from backend
 *   3) subscribe via serviceWorker.pushManager
 *   4) POST subscription JSON to /api/push/subscribe
 * Returns the raw PushSubscription (or throws).
 */
export async function subscribeToPush() {
    if (!isPushSupported()) throw new Error("Push notifications aren't supported by this browser.");

    const perm = await Notification.requestPermission();
    if (perm !== "granted") throw new Error("Notification permission denied.");

    const { data: { public_key } } = await api.get("/push/public-key");
    if (!public_key) throw new Error("Server didn't return a VAPID key.");

    const reg = await navigator.serviceWorker.ready;
    const existing = await reg.pushManager.getSubscription();
    if (existing) {
        // Already subscribed — refresh backend record and return
        const j = existing.toJSON();
        await api.post("/push/subscribe", {
            endpoint: j.endpoint,
            keys: j.keys,
            user_agent: navigator.userAgent,
        });
        return existing;
    }

    const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(public_key),
    });
    const j = subscription.toJSON();
    await api.post("/push/subscribe", {
        endpoint: j.endpoint,
        keys: j.keys,
        user_agent: navigator.userAgent,
    });
    return subscription;
}

/** Unsubscribe locally + remove from backend. */
export async function unsubscribeFromPush() {
    if (!isPushSupported()) return;
    const reg = await navigator.serviceWorker.ready;
    const existing = await reg.pushManager.getSubscription();
    if (!existing) return;
    const endpoint = existing.endpoint;
    try { await existing.unsubscribe(); } catch { /* ignore */ }
    try { await api.post("/push/unsubscribe", { endpoint }); } catch { /* ignore */ }
}

/** Fire a test push to the caller. */
export async function sendPushTest() {
    await api.post("/push/test");
}

/** Whether the current browser/service-worker already has an active subscription. */
export async function currentSubscription() {
    if (!isPushSupported()) return null;
    try {
        const reg = await navigator.serviceWorker.ready;
        return await reg.pushManager.getSubscription();
    } catch {
        return null;
    }
}
